import { Component, inject, OnInit, signal } from '@angular/core';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ApiResult } from '../../../../shared/models/api-result.model';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { CategoryService } from '../../../services/category.service';
import {
  Category,
  CreateCategory,
  UpdateCategory,
} from '../../../models/category.model';
import { SelectModule } from 'primeng/select';
import { DropdownOption } from '../../../../shared/models/dropdowm.model';
import { ToolbarModule } from "primeng/toolbar";

@Component({
  selector: 'app-category-create-or-update',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    InputTextModule,
    RouterLink,
    FormErrorComponent,
    FormServerErrorComponent,
    ProgressSpinnerModule,
    ToggleSwitchModule,
    SelectModule,
    ToolbarModule
],
  templateUrl: './category-create-or-update.component.html',
})
export class CategoryCreateOrUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private categoryService = inject(CategoryService);
  private formGroupService = inject(FormGroupService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  loading = signal(false);

  selectLoading = signal(false);

  categoriesDropdown = signal<DropdownOption[]>([]);

  categoryId = signal<string | null>(null);

  formGroup: FormGroup = this.fb.group({
    id: [null],
    name: [null, [Validators.required, Validators.maxLength(255)]],
    description: [null, [Validators.maxLength(255)]],
    parentCategoryId: [null],
    isActive: [true],
  });

  ngOnInit(): void {
    this.loadParentCategories();

    this.activatedRoute.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.categoryId.set(id);
        this.loadData(id);
      }
    });
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        const control = this.formGroup.get(key);
        control?.markAsTouched();
      });
      return;
    }
    let data = this.formGroup.getRawValue();
    if (this.formGroup.value.id) {
      data.parentCategoryId = data.parentCategoryId?.value;
      this.update(data as UpdateCategory);
    } else {
      delete data.id;
      data.parentCategoryId = data.parentCategoryId?.value;
      this.create(data as CreateCategory);
    }
  }

  private loadData(id: string): void {
    this.loading.set(true);

    this.categoryService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const category = response.data as Category;
        this.formGroup.patchValue(category);
        this.formGroup.get('parentCategoryId')?.setValue({
          label: category.parentCategoryName,
          value: category.parentCategoryId,
        });
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar a categoria',
            life: 3000,
          });
        }
      },
    });
  }

  private loadParentCategories(): void {
    this.selectLoading.set(true);
    this.categoryService.getAllDropdown().subscribe({
      next: (response: ApiResult) => {
        const options = response.data as DropdownOption[];
        this.categoriesDropdown.set(options);
        this.selectLoading.set(false);
      },
      error: (e) => {
        this.selectLoading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar as categorias pai',
            life: 3000,
          });
        }
      },
    });
  }

  private create(data: CreateCategory): void {
    this.loading.set(true);

    this.categoryService.create(data).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso!',
          detail: 'Categoria cadastrada com sucesso!',
          life: 2000,
        });

        this.loading.set(false);
        this.router.navigate(['/categories']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors.length == 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Fail',
              detail: 'Ocorreu um erro ao cadastrar a categoria',
              life: 3000,
            });
          }
        }
      },
    });
  }

  private update(data: UpdateCategory): void {
    this.loading.set(true);

    this.categoryService.update(data.id, data).subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso!',
          detail: 'Categoria atualizada com sucesso!',
          life: 2000,
        });

        this.router.navigate(['/categories']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors.length == 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Fail',
              detail: 'Ocorreu um erro ao atualizar a categoria',
              life: 3000,
            });
          }
        }
      },
    });
  }
}
